import { useEffect } from 'react';
import { toast } from 'react-toastify';
import { Alert } from '@mui/material';

import { AccessGroupClientModel, useGetAccessGroupDetails, useResetAccessGroupCredentials } from '@client-app-sdk';

import { ErrorAlert } from '@common/ui/ErrorAlert';
import { Loader } from '@common/ui/Loader';
import Markdown from '@common/ui/Markdown';
import InvalidateAction from '@common/components/InvalidateAction';
import OpenReplayObscured from '@common/utils/analytics/OpenReplayObscured';

export default function AccessGroupDetails({ group, onReset }: { group: AccessGroupClientModel; onReset: () => void }) {
  const { data, isFetching, error } = useGetAccessGroupDetails(group.id);
  const {
    resetAccessGroupCredentials,
    isLoading: resetLoading,
    isSuccess: resetSuccess,
  } = useResetAccessGroupCredentials(group.id);

  useEffect(() => {
    if (resetSuccess) {
      toast.success('Access details reset successfully');
      onReset();
    }
  }, [resetSuccess, onReset]);

  if (isFetching) {
    return <Loader size={30} />;
  }

  if (error) {
    return <ErrorAlert error={error} />;
  }

  if (!data) {
    return <Alert severity="error">Access group details not found</Alert>;
  }

  return (
    <OpenReplayObscured>
      <Markdown>{data.markdown}</Markdown>
      {data.can_reset_credentials && (
        <InvalidateAction
          loading={resetLoading}
          buttonText="Reset Credentials"
          confirmText="Are you sure you want to reset access details?"
          invalidateAction={resetAccessGroupCredentials}
        />
      )}
    </OpenReplayObscured>
  );
}
